import { ComponentState, useCallback, useContext } from "react";
import { CustomCellEditorProps } from "ag-grid-react";
import Autocomplete from "composants/autocomplete/AutoComplete";
import { Pojo } from "types/Galaxy";
import { WviContext } from "../DatatableContext";
import { getOptionsByType } from "utils/component.utils";
import { DataInteractionContext } from "hooks/useInteractions";
import { flatDataFromPopjo } from "../AgGridContainer";
import { IRowNode, ValueSetterParams } from "ag-grid-community";

export interface CellEditorAutocompleteProps {
  tableName: string;
  sjmoCode: string;
  ctrlKey: string;
  componentID: string;
  compo: ComponentState;
  columnIndex: number;
  onChange: (params: ValueSetterParams<Pojo, string>) => void;
}

export function buildCellEditorAutocomplete({
  tableName,
  sjmoCode,
  ctrlKey,
  componentID,
  columnIndex,
  compo,
  onChange
}: CellEditorAutocompleteProps) {
  return ({ value, data, colDef, rowIndex, onValueChange, api }: CustomCellEditorProps) => {
    const wviState = useContext(WviContext);
    const wviStateCol = colDef.colId ? wviState?.[data.id]?.[colDef.colId]?.type : undefined;

    const navigationParams = {
      "data-navigation-key": ctrlKey,
      "data-navigation-row": rowIndex,
      "data-navigation-column": columnIndex
    };
    const options = getOptionsByType(compo);

    const onItemChange = useCallback(
      (itemChange: Pojo | null, _name: string | undefined) => {
        onValueChange(itemChange ?? null);
      },
      [onValueChange]
    );

    const onChangeTab = useCallback(
      (itemChange: Pojo | null, _name: string | undefined) => {
        const params: ValueSetterParams<Pojo, string> = {
          data: data,
          colDef: colDef,
          node: { rowIndex: rowIndex } as IRowNode<Pojo>,
          newValue: itemChange?.id ?? null,
          oldValue: value?.id || value
        } as ValueSetterParams<Pojo, string>;
        onChange(params);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [data, colDef, rowIndex, value, onChange]
    );

    const onEnterSelect = useCallback(
      (itemChange: Pojo | null, _name: string | undefined) => {
        // On appelle directement le valueSetter avec l'ID (onValueChange stocke le Pojo entier,
        // AG-Grid passerait donc le mauvais type au backend si on laissait le flow normal).
        // stopEditing ferme explicitement le cell editor (Enter est supprimé côté AG-Grid).
        const params: ValueSetterParams<Pojo, string> = {
          data: data,
          colDef: colDef,
          node: { rowIndex: rowIndex } as IRowNode<Pojo>,
          newValue: itemChange?.id ?? null,
          oldValue: value?.id || value
        } as ValueSetterParams<Pojo, string>;
        onChange(params);
        api.stopEditing(false);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [data, colDef, rowIndex, value, onChange, api]
    );

    return (
      <DataInteractionContext.Provider value={flatDataFromPopjo(data) as Pojo}>
        <Autocomplete
          autoFocus={true}
          tableName={tableName}
          sjmoCode={sjmoCode}
          id={componentID}
          name={compo.column}
          value={value?.id || value}
          onItemChange={onItemChange}
          onChangeTab={onChangeTab}
          onEnterSelect={onEnterSelect}
          onContextMenu={() => {}}
          joinTableName={compo.joinTableName}
          joinListFields={compo.joinListFields}
          additionalClause={compo.additionalClause}
          sortClause={compo.sortClause}
          readOnly={compo.isReadonly}
          disabled={compo.isDisabled}
          controlProps={{ expanded: true }}
          style={{ width: "100%" }}
          hasLov={compo.hasLov}
          wviState={wviStateCol}
          {...navigationParams}
          {...options}
        />
      </DataInteractionContext.Provider>
    );
  };
}
